<template>
  <v-dialog :value="value" max-width="600" @input="$emit('input', $event)" @keydown.esc="close">
    <v-card v-if="contrato">
      <!-- Mismo encabezado que la vista detalle del contrato (al presionar el ID). -->
      <v-card-title class="pb-3 d-flex align-center">
        <span class="text-h6 font-weight-bold mr-2">{{ contrato.codigo }}</span>
        <span class="text-body-2 grey--text text--darken-1">{{ companyName }}</span>
        <v-spacer />
        <v-chip v-if="contrato.lineaNegocio" x-small :color="lineColor" dark class="mr-1">{{ contrato.lineaNegocio }}</v-chip>
        <ContractStatusChip :status="contrato.status" />
      </v-card-title>
      <v-divider />

      <v-card-text class="pt-4 pb-2">
        <div v-if="loading" class="text-center py-6">
          <v-progress-circular indeterminate color="primary" />
        </div>

        <template v-else-if="detail">
          <!-- Resumen: medición más reciente + NPS de la empresa -->
          <v-alert v-if="!isActive" dense outlined type="info" class="text-body-2">
            Contrato no activo: no tiene NPS actual ni cuenta para el NPS de la empresa.
          </v-alert>

          <div v-if="detail.current" class="grey lighten-4 rounded pa-3 mb-4">
            <div class="d-flex align-center">
              <v-chip small dark :color="npsColor(detail.current.summary.nps)" class="font-weight-bold mr-2">
                {{ formatNps(detail.current.summary.nps) }}
              </v-chip>
              <span class="text-body-2 font-weight-bold">{{ formatPeriod(detail.current.period) }}</span>
              <span class="text-body-2 grey--text text--darken-1 ml-1">· {{ peopleLabel(detail.current.summary.respondents) }}</span>
            </div>
            <div class="text-caption grey--text text--darken-1 mt-1">
              {{ detail.current.summary.promoters }} promotoras ·
              {{ detail.current.summary.passives }} pasivas ·
              {{ detail.current.summary.detractors }} detractoras
            </div>
          </div>
          <div v-else-if="isActive && !detail.periods.length" class="text-body-2 grey--text text--darken-1 mb-4">
            Aún no hay calificaciones.
          </div>

          <div class="d-flex align-center text-body-2 mb-4">
            <span class="grey--text text--darken-1 mr-2">NPS empresa</span>
            <template v-if="detail.company.summary">
              <v-chip x-small dark :color="npsColor(detail.company.summary.nps)" class="font-weight-bold mr-1">
                {{ formatNps(detail.company.summary.nps) }}
              </v-chip>
              <span class="grey--text text--darken-1">· {{ peopleLabel(detail.company.summary.respondents) }}</span>
            </template>
            <v-chip v-else x-small outlined class="grey--text text--darken-1">Sin NPS</v-chip>
          </div>

          <!-- Formulario de calificación (solo contratos activos) -->
          <template v-if="canEditRatings">
            <v-btn v-if="!formOpen" small outlined color="primary" class="mb-4" @click="openCreate">
              <v-icon small left>mdi-plus</v-icon> Agregar calificación
            </v-btn>
            <v-sheet v-else outlined rounded class="pa-3 mb-4">
              <v-form ref="form" @submit.prevent="submit">
                <div class="text-body-2 font-weight-bold mb-2">{{ editingId ? 'Corregir calificación' : 'Nueva calificación' }}</div>
                <v-row dense>
                  <v-col cols="12" sm="6">
                    <v-combobox
                      v-model="form.personName"
                      :items="peopleNames"
                      :search-input.sync="personSearch"
                      :rules="[required]"
                      label="Nombre *"
                      dense
                      outlined
                      @change="onPersonChosen"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field v-model="form.personRole" :rules="[required]" label="Cargo *" dense outlined />
                  </v-col>
                  <v-col cols="6">
                    <v-select v-model="form.score" :items="scoreOptions" :rules="[requiredScore]" label="Nota *" dense outlined />
                  </v-col>
                  <v-col cols="6">
                    <v-menu v-model="periodMenu" :close-on-content-click="false" offset-y min-width="auto">
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          :value="formatPeriod(form.period)"
                          :rules="[() => Boolean(form.period) || 'Obligatorio']"
                          label="Medición *"
                          readonly
                          dense
                          outlined
                          v-bind="attrs"
                          v-on="on"
                        />
                      </template>
                      <v-date-picker
                        v-model="form.period"
                        type="month"
                        locale="es"
                        :max="maxPeriod"
                        no-title
                        @input="periodMenu = false"
                      />
                    </v-menu>
                  </v-col>
                </v-row>

                <v-alert v-if="duplicateRatingId" dense outlined type="warning" class="text-body-2">
                  Esta persona ya respondió en {{ formatPeriod(form.period) }}.
                  <v-btn x-small text color="primary" @click="editExisting">Corregir la existente</v-btn>
                </v-alert>

                <div class="d-flex justify-end">
                  <v-btn text small color="primary" rounded @click="closeForm">Cancelar</v-btn>
                  <v-btn small color="primary" rounded :loading="saving" type="submit">Guardar</v-btn>
                </div>
              </v-form>
            </v-sheet>
          </template>

          <!-- Historial por medición -->
          <div v-for="period in detail.periods" :key="period.period" class="nps-period mb-3">
            <div class="d-flex align-center pb-1">
              <span class="text-body-2 font-weight-bold mr-2">{{ formatPeriod(period.period) }}</span>
              <v-chip x-small dark :color="npsColor(period.summary.nps)" class="font-weight-bold mr-1">
                {{ formatNps(period.summary.nps) }}
              </v-chip>
              <span class="text-caption grey--text text--darken-1">· {{ peopleLabel(period.summary.respondents) }}</span>
            </div>
            <v-divider class="mb-1" />
            <div v-for="rating in period.ratings" :key="rating.id" class="d-flex align-center nps-period__row text-body-2">
              <span class="font-weight-medium">{{ rating.personName }}</span>
              <span class="grey--text text--darken-1 ml-1">· {{ rating.personRole }}</span>
              <v-spacer />
              <span class="font-weight-bold mr-2">{{ rating.score }}</span>
              <template v-if="canEditRatings">
                <v-btn icon x-small @click="openEdit(rating)"><v-icon x-small>mdi-pencil</v-icon></v-btn>
                <v-btn icon x-small color="pink" @click="askDelete(rating)"><v-icon x-small>mdi-delete</v-icon></v-btn>
              </template>
            </div>
          </div>
        </template>
      </v-card-text>

      <v-card-actions class="justify-end">
        <v-btn text color="primary" rounded @click="close">Cerrar</v-btn>
      </v-card-actions>
    </v-card>

    <!-- Confirmar eliminación de una calificación -->
    <v-dialog v-model="deleteDialog" max-width="420" @keydown.esc="deleteDialog = false">
      <v-card class="pa-2">
        <v-card-title class="text-uppercase">Eliminar calificación</v-card-title>
        <hr class="my-2" />
        <v-card-text>
          ¿Está seguro de que desea eliminar la calificación de {{ deleteTarget ? deleteTarget.personName : '' }}
          en {{ deleteTarget ? formatPeriod(deleteTarget.period) : '' }}?
        </v-card-text>
        <hr class="my-2" />
        <v-card-actions class="justify-end">
          <v-btn text color="primary" rounded @click="deleteDialog = false">Cancelar</v-btn>
          <v-btn color="pink" dark rounded :loading="saving" @click="confirmDelete">Sí, eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="showSnackbar" :timeout="5000" :color="snackbarColor">
      {{ snackbarMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="showSnackbar = false">Cerrar</v-btn>
      </template>
    </v-snackbar>
  </v-dialog>
</template>

<script>
import ContractStatusChip, { CONTRACT_STATUSES } from './ContractStatusChip.vue';
import ContratoNpsService from '../services/contrato-nps.service';
import { utilsMixin } from '../mixins/utilsMixin';
import { currentPeriod, formatNps, formatPeriod, npsColor } from '../utils/nps.utils';
import { can } from '../utils/permissions';

const ACTIVE_STATUS = CONTRACT_STATUSES[0];

// Mensajes por código del backend: el texto crudo del servidor nunca llega a la pantalla.
const ERROR_MESSAGES = {
  INVALID_PERSON_NAME: 'Revisa el nombre (obligatorio, máximo 120 caracteres).',
  INVALID_PERSON_ROLE: 'Revisa el cargo (obligatorio, máximo 120 caracteres).',
  INVALID_SCORE: 'La nota debe ser un número entero de 0 a 10.',
  INVALID_PERIOD: 'La medición no puede ser un mes futuro.',
  CONTRATO_NOT_ACTIVE: 'El contrato ya no está activo: sus calificaciones son solo de consulta.',
  CONTRATO_NOT_FOUND: 'No se encontró el contrato.',
  NPS_RATING_NOT_FOUND: 'Esa calificación ya no existe. Recarga la ventana.'
};

const emptyForm = () => ({ personName: '', personRole: '', score: null, period: null });

/**
 * NPS detail of a contract: current measurement, company NPS, history by
 * measurement period, and the form to record or correct answers. Emits
 * `updated` with the contract's new list summary after every change, so the
 * Contratos table refreshes without reloading.
 */
export default {
  name: 'ContractNpsModal',
  components: { ContractStatusChip },
  mixins: [utilsMixin],
  props: {
    value: {
      type: Boolean,
      default: false
    },
    contrato: {
      type: Object,
      default: null
    },
    lineColor: {
      type: String,
      default: 'grey'
    }
  },
  data() {
    return {
      detail: null,
      loading: false,
      saving: false,
      formOpen: false,
      editingId: null,
      form: emptyForm(),
      personSearch: null,
      periodMenu: false,
      duplicateRatingId: null,
      deleteDialog: false,
      deleteTarget: null,
      scoreOptions: Array.from({ length: 11 }, (_, score) => score),
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: ''
    };
  },
  computed: {
    isActive() {
      return Boolean(this.contrato) && this.contrato.status === ACTIVE_STATUS;
    },
    canEditRatings() {
      return this.isActive && can('contracts:update');
    },
    companyName() {
      return (this.contrato && this.contrato.company && this.contrato.company.name) || '—';
    },
    peopleNames() {
      return this.detail ? this.detail.people.map(person => person.personName) : [];
    },
    maxPeriod() {
      return currentPeriod();
    }
  },
  watch: {
    value(open) {
      if (open) this.load();
    },
    // v-combobox solo confirma su valor al perder el foco; sin esto, un nombre
    // nuevo escrito justo antes de "Guardar" se perdería.
    personSearch(text) {
      if (text !== null && text !== undefined) this.form.personName = text;
    }
  },
  methods: {
    npsColor,
    formatNps,
    formatPeriod,

    async load() {
      this.detail = null;
      this.closeForm();
      this.loading = true;
      const result = await ContratoNpsService.getDetail(this.contrato.id);
      this.loading = false;
      if (result.status === 'ok') {
        this.detail = result.data;
      } else {
        this.showErrorSnackbar(this.errorMessage(result));
      }
    },

    close() {
      this.$emit('input', false);
    },

    peopleLabel(count) {
      return count === 1 ? '1 persona' : `${count} personas`;
    },

    required(value) {
      return Boolean(value && String(value).trim()) || 'Obligatorio';
    },

    requiredScore(value) {
      return (Number.isInteger(value) && value >= 0 && value <= 10) || 'Obligatorio';
    },

    openCreate() {
      const latest = this.detail.periods[0];
      this.form = { ...emptyForm(), period: latest ? latest.period : currentPeriod() };
      this.editingId = null;
      this.duplicateRatingId = null;
      this.formOpen = true;
    },

    openEdit(rating) {
      this.form = {
        personName: rating.personName,
        personRole: rating.personRole,
        score: rating.score,
        period: rating.period
      };
      this.editingId = rating.id;
      this.duplicateRatingId = null;
      this.formOpen = true;
    },

    closeForm() {
      this.formOpen = false;
      this.editingId = null;
      this.duplicateRatingId = null;
      this.form = emptyForm();
    },

    /** Fills the role when a person already registered on the contract is chosen. */
    onPersonChosen(name) {
      const person = this.detail.people.find(p => p.personName === name);
      if (person) this.form.personRole = person.personRole;
    },

    editExisting() {
      const existing = this.detail.periods
        .flatMap(period => period.ratings)
        .find(rating => rating.id === this.duplicateRatingId);
      if (existing) this.openEdit(existing);
    },

    async submit() {
      if (!this.$refs.form.validate()) return;
      this.saving = true;
      const rating = {
        personName: this.form.personName.trim(),
        personRole: this.form.personRole.trim(),
        score: this.form.score,
        period: this.form.period
      };
      const result = this.editingId
        ? await ContratoNpsService.updateRating(this.contrato.id, this.editingId, rating)
        : await ContratoNpsService.createRating(this.contrato.id, rating);
      this.saving = false;

      if (result.status === 'ok') {
        this.applyDetail(result.data);
        this.closeForm();
        this.showSuccessSnackbar('Calificación guardada');
        return;
      }
      if (result.code === 'NPS_RATING_DUPLICATE_PERSON') {
        this.duplicateRatingId = result.existingRatingId;
        return;
      }
      // El formulario queda abierto con lo ingresado, para corregir y reintentar.
      this.showErrorSnackbar(this.errorMessage(result));
    },

    askDelete(rating) {
      this.deleteTarget = rating;
      this.deleteDialog = true;
    },

    async confirmDelete() {
      this.saving = true;
      const result = await ContratoNpsService.deleteRating(this.contrato.id, this.deleteTarget.id);
      this.saving = false;
      this.deleteDialog = false;
      if (result.status === 'ok') {
        this.applyDetail(result.data);
        this.showSuccessSnackbar('Calificación eliminada');
      } else {
        this.showErrorSnackbar(this.errorMessage(result));
      }
    },

    /** Replaces the detail and tells the table the contract's new current NPS. */
    applyDetail(detail) {
      this.detail = detail;
      const current = detail.current;
      this.$emit(
        'updated',
        current ? { nps: current.summary.nps, respondents: current.summary.respondents, period: current.period } : null
      );
    },

    errorMessage(result) {
      const code = (result.fields && result.fields[0]) || result.code;
      return ERROR_MESSAGES[code] || 'No se pudo completar la acción. Intenta nuevamente.';
    }
  }
};
</script>

<style scoped lang="scss">
.nps-period__row {
  min-height: 28px;
}
</style>
